import React__default, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { OverlayTriggerStateContext, ModalOverlay, Modal, Dialog, DialogTrigger } from 'react-aria-components';
import { CloseButton } from '../atoms/Button.js';
import { isButtonComponent } from '../utils/isButtonComponent.js';
import { useContentSliderController, PrevItemButton, NextItemButton, ContentSlider, StyledScrollbar } from './ContentSlider.js';
import './Lightbox.scss';

/* Lightbox layout breakpoints (shared with Lightbox.scss via data attributes) */
const LANDSCAPE_HEIGHT = 660;
const COMPACT_WIDTH = 860;
const COMPACT_HEIGHT = 520;
const BACKDROP_CLASSES = new Set([
    'lightbox-dialog--rev',
    'lightbox-main-view--rev',
    'lightbox-shown-item--rev',
    'lightbox-shown-item-slot--rev',
]);
// ─── Lightbox ───────────────────────────────────────────────────────────────
const Lightbox = ({ items, selectedIndex: controlledIndex, onSelectedIndexChange, hasCloseButton = true, hasItemStrip = true, isDismissable = true, isKeyboardDismissDisabled = false, className, ariaLabel, }) => {
    const overlayState = React__default.useContext(OverlayTriggerStateContext);
    const [internalIndex, setInternalIndex] = useState(0);
    const isControlled = controlledIndex !== undefined;
    const activeIndex = isControlled ? controlledIndex : internalIndex;
    // Sync internal state when controlled prop changes (e.g. on open)
    useEffect(() => {
        if (isControlled)
            setInternalIndex(controlledIndex);
    }, [isControlled, controlledIndex]);
    // Landscape mode: orientation is landscape AND viewport is short enough
    // that the horizontal strip would crowd the shown item.
    const landscapeQuery = `(orientation: landscape) and (max-height: ${LANDSCAPE_HEIGHT}px)`;
    const compactQuery = `(max-width: ${COMPACT_WIDTH}px), (max-height: ${COMPACT_HEIGHT}px)`;
    const [isLandscape, setIsLandscape] = useState(() => typeof window !== 'undefined' && window.matchMedia(landscapeQuery).matches);
    const [isCompact, setIsCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia(compactQuery).matches);
    useEffect(() => {
        const landscapeMq = window.matchMedia(landscapeQuery);
        const compactMq = window.matchMedia(compactQuery);
        const onLandscape = (e) => setIsLandscape(e.matches);
        const onCompact = (e) => setIsCompact(e.matches);
        landscapeMq.addEventListener('change', onLandscape);
        compactMq.addEventListener('change', onCompact);
        return () => {
            landscapeMq.removeEventListener('change', onLandscape);
            compactMq.removeEventListener('change', onCompact);
        };
    }, [landscapeQuery, compactQuery]);
    const viewport = [isLandscape ? 'landscape' : 'portrait', isCompact ? 'compact' : '']
        .filter(Boolean)
        .join(' ');
    // Keep visited items mounted so navigating back doesn't trigger a
    // fresh loading state. Also pre-mount the 2 nearest neighbours so
    // their images start loading before the user navigates to them.
    const mountedIndicesRef = useRef(new Set());
    for (let offset = -2; offset <= 2; offset++) {
        const idx = activeIndex + offset;
        if (idx >= 0 && idx < items.length)
            mountedIndicesRef.current.add(idx);
    }
    const mountedIndices = mountedIndicesRef.current;
    // Imperatively animate slot opacity via Web Animations API so rapid
    // navigation always triggers a visible crossfade. CSS transitions can
    // silently skip when the browser batches multiple attribute changes
    // into a single frame.
    const slotRefs = useRef(new Map());
    const prevIndexRef = useRef(activeIndex);
    const animationsRef = useRef(new Map());
    useLayoutEffect(() => {
        const prev = prevIndexRef.current;
        prevIndexRef.current = activeIndex;
        if (prev === activeIndex)
            return;
        const duration = 250; // matches --rev-duration-moderato
        // Cancel any in-flight animation on the entering slot so we always
        // start from its current visual opacity.
        const prevEnterAnim = animationsRef.current.get(activeIndex);
        if (prevEnterAnim)
            prevEnterAnim.cancel();
        const prevLeaveAnim = animationsRef.current.get(prev);
        if (prevLeaveAnim)
            prevLeaveAnim.cancel();
        // Animate the entering slot: always from fully transparent
        const enterEl = slotRefs.current.get(activeIndex);
        if (enterEl) {
            const enterAnim = enterEl.animate([{ opacity: 0 }, { opacity: 1 }], {
                duration,
                easing: 'cubic-bezier(0.3, 0, 0, 1)', // curveOut
                fill: 'forwards',
            });
            animationsRef.current.set(activeIndex, enterAnim);
            enterAnim.finished
                .then(() => {
                enterAnim.commitStyles();
                enterAnim.cancel();
                animationsRef.current.delete(activeIndex);
            })
                .catch(() => { });
        }
        // Animate the leaving slot: always from fully opaque
        const leaveEl = slotRefs.current.get(prev);
        if (leaveEl) {
            const leaveAnim = leaveEl.animate([{ opacity: 1 }, { opacity: 0 }], {
                duration,
                easing: 'cubic-bezier(1, 0, 0.7, 1)', // curveIn
                fill: 'forwards',
            });
            animationsRef.current.set(prev, leaveAnim);
            leaveAnim.finished
                .then(() => {
                leaveAnim.commitStyles();
                leaveAnim.cancel();
                animationsRef.current.delete(prev);
            })
                .catch(() => { });
        }
    }, [activeIndex]);
    const stripSlider = useContentSliderController();
    const stripTrackPadding = '32';
    const stripScrollToIndexRef = useRef(stripSlider.contextValue.scrollToIndex);
    useEffect(() => {
        stripScrollToIndexRef.current = stripSlider.contextValue.scrollToIndex;
    }, [stripSlider.contextValue.scrollToIndex]);
    const setActiveIndex = useCallback((index) => {
        const clamped = Math.max(0, Math.min(index, items.length - 1));
        if (!isControlled)
            setInternalIndex(clamped);
        onSelectedIndexChange?.(clamped);
        stripScrollToIndexRef.current(clamped);
    }, [isControlled, items.length, onSelectedIndexChange]);
    const goPrev = useCallback(() => {
        if (activeIndex > 0)
            setActiveIndex(activeIndex - 1);
    }, [activeIndex, setActiveIndex]);
    const goNext = useCallback(() => {
        if (activeIndex < items.length - 1)
            setActiveIndex(activeIndex + 1);
    }, [activeIndex, items.length, setActiveIndex]);
    const hasPrev = activeIndex > 0;
    const hasNext = activeIndex < items.length - 1;
    // Build a lightweight context for the prev/next buttons so they can
    // use the existing PrevItemButton / NextItemButton components.
    const navContext = React__default.useMemo(() => ({
        ...stripSlider.contextValue,
        prevItem: goPrev,
        nextItem: goNext,
        hasPrevItem: hasPrev,
        hasNextItem: hasNext,
    }), [stripSlider.contextValue, goPrev, goNext, hasPrev, hasNext]);
    // Close lightbox when clicking the backdrop — the dialog, main-view, or
    // shown-item containers (the visual "empty space" around actual content).
    const handleBackdropClick = useCallback((e) => {
        if (!isDismissable)
            return;
        const target = e.target;
        if (target.classList && [...target.classList].some((c) => BACKDROP_CLASSES.has(c))) {
            overlayState.close();
        }
    }, [isDismissable, overlayState]);
    // Arrow keys always change selection regardless of focus target.
    // Landscape uses up/down, portrait uses left/right.
    useEffect(() => {
        const handleKeyDown = (e) => {
            const prevKeys = isLandscape ? ['ArrowUp', 'ArrowLeft'] : ['ArrowLeft'];
            const nextKeys = isLandscape ? ['ArrowDown', 'ArrowRight'] : ['ArrowRight'];
            if (prevKeys.includes(e.key)) {
                e.preventDefault();
                goPrev();
            }
            else if (nextKeys.includes(e.key)) {
                e.preventDefault();
                goNext();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isLandscape, goPrev, goNext]);
    return (React__default.createElement(ModalOverlay, { "data-rev-component": 'lightbox', className: className, isDismissable: isDismissable, isKeyboardDismissDisabled: isKeyboardDismissDisabled },
        React__default.createElement(Modal, { className: 'lightbox-modal--rev' },
            React__default.createElement(Dialog, { className: 'lightbox-dialog--rev', "aria-label": ariaLabel ?? 'Lightbox', "data-rev-viewport": viewport, onClick: handleBackdropClick },
                hasCloseButton && (React__default.createElement(CloseButton, { className: 'lightbox-close-button--rev', variant: 'filled', isInverted: true, "aria-label": 'Close lightbox', size: 'large', onPress: () => overlayState.close() })),
                React__default.createElement("div", { className: 'lightbox-main-view--rev' },
                    !hasItemStrip && items.length > 1 && (React__default.createElement(PrevItemButton, { contextValue: navContext, className: 'lightbox-nav-button--rev' })),
                    React__default.createElement("div", { className: 'lightbox-shown-item--rev' }, items.map((item, i) => (React__default.createElement("div", { key: i, ref: (el) => {
                            if (el)
                                slotRefs.current.set(i, el);
                            else
                                slotRefs.current.delete(i);
                        }, className: 'lightbox-shown-item-slot--rev', "data-rev-active": i === activeIndex || undefined, "aria-hidden": i !== activeIndex }, mountedIndices.has(i) ? item : null)))),
                    !hasItemStrip && items.length > 1 && (React__default.createElement(NextItemButton, { contextValue: navContext, className: 'lightbox-nav-button--rev' }))),
                hasItemStrip && items.length > 1 && (React__default.createElement("div", { className: 'lightbox-item-strip--rev', style: {
                        '--contentSlider-trackPadding': `${stripTrackPadding}px`,
                    } },
                    React__default.createElement(PrevItemButton, { contextValue: navContext, className: 'lightbox-nav-button--rev' }),
                    React__default.createElement(ContentSlider, { items: items, itemsSpacing: '8', axis: isLandscape ? 'y' : 'x', onItemPress: setActiveIndex, selectedIndex: activeIndex, controller: stripSlider, trackPadding: `${stripTrackPadding}px`, isKeyboardDisabled: true, initialScrollIndex: activeIndex }),
                    React__default.createElement(NextItemButton, { contextValue: navContext, className: 'lightbox-nav-button--rev' }),
                    (stripSlider.contextValue.hasPrevItem ||
                        stripSlider.contextValue.hasNextItem) && (React__default.createElement(StyledScrollbar, { contextValue: stripSlider.contextValue, className: 'lightbox-item-strip-scrollbar--rev', isInverted: true }))))))));
};
// ─── LightboxTrigger ────────────────────────────────────────────────────────
const LightboxTrigger = ({ children, isOpen, onOpenChange }) => {
    const enhancedChildren = React__default.Children.map(children, (child, index) => {
        if (index === 0 && React__default.isValidElement(child)) {
            if (isButtonComponent(child)) {
                return React__default.cloneElement(child, {
                    ...child.props,
                    ariaHaspopup: 'dialog',
                });
            }
        }
        return child;
    });
    return (React__default.createElement(DialogTrigger, { isOpen: isOpen, onOpenChange: onOpenChange }, enhancedChildren));
};

export { Lightbox, LightboxTrigger };
//# sourceMappingURL=Lightbox.js.map
